import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import ConnectedIntlProvider from 'Containers/ConnectedIntlProvider';
import * as usersActions from 'Store/users/actions';
import {
  initializeAmplitudeForWeb,
  setAmplitudeUserForWeb,
} from 'Store/stats/actions';
import * as reducers from 'Store/reducers';
import changeActiveLanguage from 'Store/locales/actions';
import { SUPPORTED_LANGUAGES } from 'Store/locales/constants';
import * as helpscout from 'Components/ContactLink/helpscout';
import { getCookie } from '../utilities';

import SiteHeader from 'Components/SiteHeader';
import SiteFooter from 'Components/SiteFooter';
import DocumentHead from 'Containers/DocumentHead';
import ReadOnlyModal from 'Components/ReadOnlyModal';
import Error from 'Containers/Error';
import { ROUTES } from 'Settings/route-strings';

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      requestedActiveUser: false,
      sentAmplitudeRequest: false,
    };
  }

  componentDidMount() {
    this.props.checkCookieForAuthToken();
    this.props.checkCookieForActiveSchool();
    this.props.initializeAmplitudeForWeb();
    this.props.fetchActiveUserIfNeeded();
    if (typeof window.Beacon === 'undefined') {
      helpscout.initialize();
      window.Beacon('init', 'ce9cbe87-941a-4951-8fb0-a22a190c9b2b');
    }

    const cookieLocale = getCookie('tbsLocale');
    if (cookieLocale && SUPPORTED_LANGUAGES.includes(cookieLocale)) {
      this.props.changeActiveLanguage(cookieLocale);
    }
  }

  componentDidUpdate(prevProps) {
    const { authToken, isAuthTokenExpired, activeUser, activeSchoolId } =
      this.props;

    /* 
      NOTE: this block was causing race-condition errors when logging in users after they had
            paid to reactivate their accounts 
      TODO: confirm that this is safe to remove
    */

    // If there is no active user in the store, fetch it.
    // if (authToken && !isAuthTokenExpired && !this.state.requestedActiveUser) {
    //   this.setState({ requestedActiveUser: true }, () =>
    //     this.props.fetchActiveUserIfNeeded()
    //   );
    // }

    // If there is an expired token, log the user out.
    if (
      authToken &&
      isAuthTokenExpired === true &&
      isAuthTokenExpired !== prevProps.isAuthTokenExpired
    ) {
      this.props.postUserLogout({
        expiredToken: true,
      });
    }

    // If we have a user, set their amplitude and helpscout user context.
    if (activeUser && !this.state.sentAmplitudeRequest) {
      helpscout.identifyUser(activeUser, activeSchoolId);
      this.props.setAmplitudeUserForWeb();
    }

    // If the user changes their page, make sure you scroll to the top.
    if (prevProps.location.pathname !== this.props.location.pathname)
      window.scrollTo(0, 0);
  }

  render() {
    const { location } = this.props;
    const showError =
      this.state.requestedActiveUser &&
      !this.props.isFetchingActiveUser &&
      this.props.authToken &&
      !this.props.activeUser;

    return (
      <ConnectedIntlProvider>
        <div className="Page">
          <DocumentHead />
          <SiteHeader isSticky={location.pathname === ROUTES.INDEX} />
          {showError ? (
            <Error type="apiLoadError" code="500" />
          ) : (
            this.props.children
          )}
          <SiteFooter />
          {this.props.showReadOnlyModal ? <ReadOnlyModal /> : null}
        </div>
      </ConnectedIntlProvider>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    authToken: reducers.getAuthToken(state),
    isAuthTokenExpired: reducers.isAuthTokenExpired(state),
    isFetchingActiveUser: reducers.getIsFetchingActiveUser(state),
    activeUser: reducers.getActiveUser(state),
    activeSchoolId: reducers.getActiveSchoolId(state),
    showReadOnlyModal: reducers.isModalActive(state, 'read-only-mode'),
  };
};

App.propTypes = {
  authToken: PropTypes.string,
  isAuthTokenExpired: PropTypes.bool,
  isFetchingActiveUser: PropTypes.bool.isRequired,
  activeUser: PropTypes.object,
  showReadOnlyModal: PropTypes.bool.isRequired,
  checkCookieForAuthToken: PropTypes.func.isRequired,
  checkCookieForActiveSchool: PropTypes.func.isRequired,
  initializeAmplitudeForWeb: PropTypes.func.isRequired,
  fetchActiveUserIfNeeded: PropTypes.func.isRequired,
  postUserLogout: PropTypes.func.isRequired,
  setAmplitudeUserForWeb: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    action: PropTypes.string,
  }).isRequired,
  changeActiveLanguage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, {
  ...usersActions,
  initializeAmplitudeForWeb,
  setAmplitudeUserForWeb,
  changeActiveLanguage,
})(withRouter(App));
